import { useCallback, useEffect, useRef, useState } from "react";
import type { DocumentNode } from "graphql";
import { useQuery } from "@apollo/client/react";

/**
 * Read a list one page at a time, growing only as far as somebody scrolls.
 *
 * The counterpart to `useAllRows`, and the difference is the size of the collection rather than
 * taste. `useAllRows` walks a list to the end and hands back the whole thing, which is right for
 * 620 videos and 113 courses because those pages search and count over the array they hold. Point
 * it at 85,371 users and it does exactly what it promises: eighty-five thousand rows in the
 * browser, four hundred round trips, and a tab that stops responding.
 *
 * So this one never holds the whole collection. It fetches the first page, and fetches the next
 * only when `loadMore` is called — which `useInfiniteScroll` wires to a sentinel near the bottom of
 * the list. Filtering and counting move to the server, where they belong for a set this size;
 * `hasMore` says whether the end has been reached, and the total comes from a count query rather
 * than from `rows.length`.
 *
 * Requires an appending merge policy on the field — see `offsetLimitPagination` in `lib/apollo`.
 * Without one the cache overwrites on each page and the list never grows past one screen.
 */

/** `MAX_LIMIT` in `common/database/repositories/_pagination.ts`. */
const MAX_PAGE_SIZE = 200;

/**
 * Rows per request.
 *
 * Smaller than `useAllRows` uses, because here the first page is what the person waits for and
 * every page after it arrives while they are already reading. Fifty fills a table on a tall
 * monitor with enough margin that the next fetch starts before they reach the bottom.
 *
 * Exported because anything issuing the *same* query outside this hook has to send the same
 * variables — the merge policy keys on the filter, so a sibling query with a different `limit`
 * writes its rows over the window at whatever offset it claims.
 */
export const DEFAULT_PAGE_SIZE = 50;

interface UseWindowedRowsResult<TRow> {
    rows: TRow[];
    /** The first page is still in flight — the only time a table has nothing to show. */
    loading: boolean;
    /** A further page is in flight. Render a footer spinner, never a full-page skeleton. */
    loadingMore: boolean;
    /** False once a short page has come back: there is nothing left to ask for. */
    hasMore: boolean;
    loadMore: () => void;
    refetch: () => Promise<unknown>;
}

interface UseWindowedRowsOptions {
    variables?: Record<string, unknown>;
    skip?: boolean;
    pageSize?: number;
}

export const useWindowedRows = <TRow>(
    document: DocumentNode,
    select: (data: unknown) => TRow[] | undefined,
    { variables = {}, skip = false, pageSize = DEFAULT_PAGE_SIZE }: UseWindowedRowsOptions = {},
): UseWindowedRowsResult<TRow> => {
    const size = Math.min(pageSize, MAX_PAGE_SIZE);
    const { data, loading, refetch, fetchMore } = useQuery(document, {
        variables: { ...variables, limit: size, offset: 0 },
        skip,
    });

    const [
        loadingMore,
        setLoadingMore,
    ] = useState(false);
    const [
        exhausted,
        setExhausted,
    ] = useState(false);

    const rows = select(data) ?? [];
    const rowCount = rows.length;

    /*
     * Same ref discipline as `useAllRows`, and for the same reason: Apollo hands back a new
     * `fetchMore` on every render and flips `loading` for the duration of each call, so anything
     * that closes over them goes stale mid-flight.
     */
    const fetchMoreRef = useRef(fetchMore);
    fetchMoreRef.current = fetchMore;
    const selectRef = useRef(select);
    selectRef.current = select;
    const variablesRef = useRef(variables);
    variablesRef.current = variables;
    const rowCountRef = useRef(0);
    rowCountRef.current = rowCount;
    const loadingMoreRef = useRef(false);
    loadingMoreRef.current = loadingMore;

    // A new filter is a different list: forget that the old one had run out.
    const key = JSON.stringify(variables);
    useEffect(() => {
        setExhausted(false);
    }, [key]);

    const loadMore = useCallback(() => {
        // Guarded rather than debounced at the call site: a scroll sentinel fires repeatedly while
        // it is on screen, and without this each firing would request the same offset again.
        if (loadingMoreRef.current || exhausted || skip) return;
        const offset = rowCountRef.current;
        if (offset === 0) return;

        setLoadingMore(true);
        void (async () => {
            try {
                const result = await fetchMoreRef.current({
                    variables: { ...variablesRef.current, limit: size, offset },
                });
                const fetched = selectRef.current(result.data)?.length ?? 0;
                if (fetched < size) setExhausted(true);
            } catch {
                // Stop asking. A page that failed will not succeed on the next scroll event, and
                // retrying forever turns one error into a request loop.
                setExhausted(true);
            } finally {
                setLoadingMore(false);
            }
        })();
    }, [
        exhausted,
        skip,
        size,
    ]);

    return {
        rows,
        loading: loading && rowCount === 0,
        loadingMore,
        // A first page shorter than requested is already the whole list.
        hasMore: !exhausted && !skip && rowCount > 0 && rowCount % size === 0,
        loadMore,
        refetch,
    };
};
